import type { TileState } from '../tileManager';
import { Minimap } from './minimap';

export interface GameHudState {
  mode: 'fly' | 'walk' | 'drive';
  /** Player car: km/h (signed), gear (0 = first, -1 reverse), revs 0 … 1. */
  car: { kmh: number; gear: number; rev: number } | null;
  tiles: Iterable<TileState>;
  /** Where the player is (generation cm) and the view heading (fly camera convention). */
  gx: number;
  gy: number;
  yaw: number;
  /** Metres above the ground (fly mode zooms the radar out with height). */
  height: number;
  /** A status line (loading colliders, can't start…), or ''. */
  status: string;
}

/**
 * The player's HUD: the radar (bottom left), a speedometer with gear and rev bar while driving
 * (bottom right), a mode badge and a status line, and control hints that fade after a few seconds.
 */
export class GameHud {
  readonly el: HTMLDivElement;
  readonly minimap: Minimap;
  private readonly speedo: HTMLDivElement;
  private readonly speed: HTMLSpanElement;
  private readonly gear: HTMLSpanElement;
  private readonly rev: HTMLDivElement;
  private readonly hint: HTMLDivElement;
  private readonly status: HTMLDivElement;
  private hintUntil = 0;
  private lastText = '';

  constructor(parent: HTMLElement) {
    this.el = document.createElement('div');
    this.el.className = 'game-hud';
    this.minimap = new Minimap(this.el);
    this.speedo = div('speedo', this.el);
    this.speed = document.createElement('span');
    this.speed.className = 'kmh';
    const unit = document.createElement('span');
    unit.className = 'unit';
    unit.textContent = 'km/h';
    this.gear = document.createElement('span');
    this.gear.className = 'gear';
    const bar = div('revbar', this.speedo);
    this.rev = div('rev', bar);
    this.speedo.prepend(this.speed, unit, this.gear);
    this.hint = div('hint', this.el);
    this.status = div('status', this.el);
    parent.appendChild(this.el);
  }

  set visible(on: boolean) {
    this.el.style.display = on ? '' : 'none';
  }

  /** Show the controls for a moment (fades out after `seconds`). */
  showHint(html: string, seconds = 8): void {
    this.hint.innerHTML = html;
    this.hint.classList.add('on');
    this.hintUntil = performance.now() + seconds * 1000;
  }

  update(now: number, s: GameHudState): void {
    if (this.hintUntil && now > this.hintUntil) {
      this.hint.classList.remove('on');
      this.hintUntil = 0;
    }
    const car = s.car;
    this.speedo.style.display = car ? '' : 'none';
    if (car) {
      const kmh = Math.round(Math.abs(car.kmh));
      const gear = car.kmh < -3 ? 'R' : String(car.gear + 1);
      const text = `${kmh}|${gear}`;
      if (text !== this.lastText) {
        this.lastText = text;
        this.speed.textContent = String(kmh);
        this.gear.textContent = gear;
      }
      this.rev.style.transform = `scaleX(${Math.max(0.02, Math.min(1, car.rev)).toFixed(3)})`;
      this.rev.classList.toggle('red', car.rev > 0.88);
    }
    if (this.status.textContent !== s.status) this.status.textContent = s.status;
    this.status.style.display = s.status ? '' : 'none';
    // Radar range: walking 110 m, driving 140–320 m with speed, flying by height.
    const want = s.mode === 'walk' ? 110 : s.mode === 'drive' ? 140 + Math.min(180, Math.abs(car?.kmh ?? 0) * 2) : Math.min(1200, 160 + s.height * 2);
    this.minimap.update(now, s.tiles, s.gx, s.gy, s.yaw, want);
  }
}

function div(cls: string, parent: HTMLElement): HTMLDivElement {
  const d = document.createElement('div');
  d.className = cls;
  parent.appendChild(d);
  return d;
}
